import ComparePage, { CompareRow, CompareFaq, Reason } from '@comp/compare/compare-template'

interface PageContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  reasons: Reason[]
  tableTitle: [string, string]
  tableBody: string
  rows: CompareRow[]
  faqs: CompareFaq[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    seoTitle: 'ADFS Alternative | Monofor | Modern federation, AD stays put',
    seoDescription:
      'Looking for an ADFS alternative? Monosign delivers SAML 2.0, OIDC, and WS-Fed federation on top of your existing Active Directory, self-hosted or cloud, with MFA, conditional access, and lifecycle built in.',
    heroEyebrow: 'ADFS alternative',
    heroTitle: ['AD FS is winding down.', 'Your Active Directory isn’t.'],
    heroBody:
      'Microsoft’s investment has moved to Entra ID, leaving AD FS in maintenance mode, but not every organization can or wants to move identity to a cloud-only IdP. Monosign layers modern SAML 2.0, OIDC, and WS-Fed federation on top of the Active Directory you already run, adds the MFA, conditional access, and lifecycle AD FS never had, and deploys in minutes.',
    reasons: [
      {
        title: 'AD stays the source of truth',
        body: 'AD FS’s whole appeal was being native to Active Directory. Moving to a cloud-only IdP shifts identity’s center of gravity out of your directory. Monosign federates on top of the AD you already run: users, groups, and passwords stay where they are; you gain the modern protocols.',
      },
      {
        title: 'Everything AD FS did, and what it never could',
        body: 'WS-Fed for the legacy relying parties, SAML 2.0 and OIDC for everything modern, Kerberos for integrated Windows sign-on. Then the parts AD FS left to add-ons or never had: MFA up to passkeys, conditional access policies, SCIM provisioning, and a 7,000+ app catalog.',
      },
      {
        title: 'Your infrastructure or ours',
        body: 'Monosign runs self-hosted on your infrastructure or as SaaS: same product, same price. Teams that cannot or will not go all-in on a cloud IdP keep full control of where identity lives, without staying on software that is no longer moving forward.',
      },
    ],
    tableTitle: ['Monofor vs AD FS,', 'feature by feature.'],
    tableBody:
      'At the federation core the two overlap; that is the point, migration is low-risk. The difference is everything AD FS never grew: MFA, conditional access, lifecycle, and a product that is still being invested in.',
    rows: [
      {
        feature: 'Actively developed product',
        detail: 'Roadmap, releases, new capabilities',
        monofor: true,
        competitor: 'Maintenance mode',
      },
      {
        feature: 'SAML 2.0 federation',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'OpenID Connect / OAuth 2.0',
        detail: 'Modern app and API flows',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'WS-Fed for legacy relying parties',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Kerberos / integrated Windows authentication',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Active Directory as source of truth',
        detail: 'No directory migration required',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'MFA built in',
        detail: 'TOTP, push, FIDO2 passkeys, adaptive step-up',
        monofor: true,
        competitor: 'Via add-ons',
      },
      {
        feature: 'Conditional access policies',
        detail: 'Risk, network, device, and time conditions',
        monofor: true,
        competitor: 'Basic claim rules',
      },
      {
        feature: 'User lifecycle & SCIM provisioning',
        detail: 'Joiner-mover-leaver automation',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Pre-built app catalog',
        detail: '7,000+ SAML / OIDC integrations',
        monofor: true,
        competitor: 'Manual relying-party setup',
      },
      {
        feature: 'Self-hosted or cloud',
        detail: 'Same product, same price',
        monofor: true,
        competitor: 'Self-hosted only (Windows Server)',
      },
      {
        feature: 'Deploy in minutes',
        monofor: true,
        competitor: 'Farm + proxy topology',
      },
    ],
    faqs: [
      {
        q: 'Can we keep Active Directory as the source of truth?',
        a: 'Yes. Monosign is designed to federate on top of your existing directory. Users, groups, and passwords stay in AD; Monosign synchronizes continuously and layers modern federation, MFA, and conditional access on top (no directory migration required).',
      },
      {
        q: 'Does Monosign support the WS-Fed applications our AD FS farm serves today?',
        a: 'Yes. Monosign speaks WS-Fed alongside SAML 2.0 and OIDC/OAuth 2.0, so relying parties built for AD FS (including older Microsoft-stack applications) can be re-pointed without code changes.',
      },
      {
        q: 'Can we migrate gradually, or is it a cutover weekend?',
        a: 'Gradually. Federation migrations are naturally incremental: each relying party is re-pointed from AD FS to Monosign one at a time. Users keep signing in with their AD credentials throughout, and both systems can run side by side until the last app moves.',
      },
      {
        q: 'Do we have to move to the cloud?',
        a: 'No. Monosign deploys self-hosted on your infrastructure or as SaaS: same product, same price. That is exactly the gap this migration usually exposes: Microsoft’s successor to AD FS is cloud-only, and Monofor is not.',
      },
      {
        q: 'What do we gain beyond replacing AD FS?',
        a: 'MFA from TOTP to passkeys, conditional access policies, SCIM provisioning and lifecycle automation, a 7,000+ app catalog, and audit reporting: capabilities that in the AD FS world required add-ons or simply did not exist.',
      },
    ],
  },
  tr: {
    seoTitle: 'ADFS Alternatifi | Monofor | Modern federasyon, AD yerinde kalır',
    seoDescription:
      'ADFS alternatifi mi arıyorsunuz? Monosign, mevcut Active Directory’nizin üzerinde SAML 2.0, OIDC ve WS-Fed federasyonu sunar; kendi altyapınızda veya bulutta, MFA, koşullu erişim ve yaşam döngüsü dahil.',
    heroEyebrow: 'ADFS alternatifi',
    heroTitle: ['AD FS emekliye ayrılıyor.', 'Active Directory’niz değil.'],
    heroBody:
      'Microsoft yatırımını Entra ID’ye kaydırdı ve AD FS bakım modunda; ama her kurum kimliği yalnızca buluttaki bir IdP’ye taşıyamaz ya da taşımak istemez. Monosign, zaten işlettiğiniz Active Directory’nin üzerine modern SAML 2.0, OIDC ve WS-Fed federasyonunu ekler; AD FS’in hiç sahip olmadığı MFA, koşullu erişim ve yaşam döngüsünü de getirir ve dakikalar içinde kurulur.',
    reasons: [
      {
        title: 'AD kaynak sistem olarak kalır',
        body: 'AD FS’in bütün cazibesi Active Directory’ye yerel olmasıydı. Yalnızca buluttaki bir IdP’ye geçmek, kimliğin ağırlık merkezini dizininizin dışına taşır. Monosign ise mevcut AD’nizin üzerinde federasyon kurar: kullanıcılar, gruplar ve parolalar yerinde kalır; siz modern protokolleri kazanırsınız.',
      },
      {
        title: 'AD FS’in yaptığı her şey ve hiç yapamadıkları',
        body: 'Eski uygulamalar için WS-Fed, modern her şey için SAML 2.0 ve OIDC, entegre Windows oturumu için Kerberos. Üstüne AD FS’in eklentilere bıraktığı ya da hiç sahip olmadığı parçalar: passkey’lere uzanan MFA, koşullu erişim politikaları, SCIM ile kullanıcı sağlama ve 7.000+ uygulamalık katalog.',
      },
      {
        title: 'Altyapı sizin veya bizim',
        body: 'Monosign kendi altyapınızda self-hosted veya SaaS olarak çalışır: aynı ürün, aynı fiyat. Buluttaki bir IdP’ye tamamen geçemeyen ya da geçmek istemeyen ekipler, artık ilerlemeyen bir yazılımda kalmadan kimliğin nerede yaşayacağının kontrolünü elinde tutar.',
      },
    ],
    tableTitle: ['Monofor ve AD FS,', 'özellik özellik.'],
    tableBody:
      'Federasyon çekirdeğinde ikisi örtüşür: mesele de bu; geçiş düşük riskli. Fark, AD FS’in hiç geliştiremediği her şeyde: MFA, koşullu erişim, yaşam döngüsü ve hâlâ yatırım yapılan bir ürün.',
    rows: [
      {
        feature: 'Aktif olarak geliştirilen ürün',
        detail: 'Yol haritası, sürümler, yeni yetenekler',
        monofor: true,
        competitor: 'Bakım modunda',
      },
      {
        feature: 'SAML 2.0 federasyonu',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'OpenID Connect / OAuth 2.0',
        detail: 'Modern uygulama ve API akışları',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Eski uygulamalar için WS-Fed',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Kerberos / entegre Windows kimlik doğrulama',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Kaynak sistem olarak Active Directory',
        detail: 'Dizin taşıma gerekmez',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'MFA dahil',
        detail: 'TOTP, push, FIDO2 passkey, uyarlanabilir ek doğrulama',
        monofor: true,
        competitor: 'Eklentilerle',
      },
      {
        feature: 'Koşullu erişim politikaları',
        detail: 'Risk, ağ, cihaz ve zaman koşulları',
        monofor: true,
        competitor: 'Temel claim kuralları',
      },
      {
        feature: 'Kullanıcı yaşam döngüsü ve SCIM ile sağlama',
        detail: 'İşe giriş-değişiklik-ayrılış otomasyonu',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Hazır uygulama kataloğu',
        detail: '7.000+ SAML / OIDC entegrasyonu',
        monofor: true,
        competitor: 'Elle relying party kurulumu',
      },
      {
        feature: 'Kendi altyapınızda veya bulutta',
        detail: 'Aynı ürün, aynı fiyat',
        monofor: true,
        competitor: 'Yalnızca self-hosted (Windows Server)',
      },
      {
        feature: 'Dakikalar içinde kurulum',
        monofor: true,
        competitor: 'Farm + proxy topolojisi',
      },
    ],
    faqs: [
      {
        q: 'Active Directory’yi kaynak sistem olarak tutabilir miyiz?',
        a: 'Evet. Monosign, mevcut dizininizin üzerinde federasyon kurmak için tasarlandı. Kullanıcılar, gruplar ve parolalar AD’de kalır; Monosign sürekli eşitler ve üstüne modern federasyon, MFA ve koşullu erişimi ekler; dizin taşıma gerekmez.',
      },
      {
        q: 'Monosign, bugün AD FS çiftliğimizin hizmet verdiği WS-Fed uygulamalarını destekliyor mu?',
        a: 'Evet. Monosign, SAML 2.0 ve OIDC/OAuth 2.0’ın yanında WS-Fed de konuşur; AD FS için yazılmış relying party’ler (eski Microsoft yığını uygulamaları dahil) kod değişikliği olmadan yeniden yönlendirilebilir.',
      },
      {
        q: 'Kademeli geçebilir miyiz, yoksa bir hafta sonu her şey mi taşınır?',
        a: 'Kademeli. Federasyon geçişleri doğası gereği artımlıdır: her relying party, AD FS’ten Monosign’a tek tek yeniden yönlendirilir. Kullanıcılar süreç boyunca AD kimlik bilgileriyle oturum açmaya devam eder ve son uygulama taşınana kadar iki sistem yan yana çalışabilir.',
      },
      {
        q: 'Buluta geçmek zorunda mıyız?',
        a: 'Hayır. Monosign kendi altyapınızda self-hosted veya SaaS olarak kurulur: aynı ürün, aynı fiyat. Bu geçişin genellikle ortaya çıkardığı boşluk tam olarak bu: Microsoft’un AD FS’e biçtiği halef yalnızca bulutta; Monofor değil.',
      },
      {
        q: 'AD FS’i değiştirmenin ötesinde ne kazanırız?',
        a: 'TOTP’den passkey’lere MFA, koşullu erişim politikaları, SCIM ile sağlama ve yaşam döngüsü otomasyonu, 7.000+ uygulamalık katalog ve denetim raporlaması: AD FS dünyasında eklenti gerektiren ya da hiç var olmayan yetenekler.',
      },
    ],
  },
}

type Props = { locale: string }

export default function AdfsAlternativePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <ComparePage
      competitor="AD FS"
      locale={locale}
      bilingual
      path="/compare/adfs-alternative"
      seoTitle={c.seoTitle}
      seoDescription={c.seoDescription}
      heroEyebrow={c.heroEyebrow}
      heroTitle={
        <>
          {c.heroTitle[0]}
          <br />
          {c.heroTitle[1]}
        </>
      }
      heroBody={c.heroBody}
      reasons={c.reasons}
      tableTitle={
        <>
          {c.tableTitle[0]}
          <br />
          {c.tableTitle[1]}
        </>
      }
      tableBody={c.tableBody}
      rows={c.rows}
      faqs={c.faqs}
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
